/**
 * The attachment data model — pure functions, no React, no I/O.
 *
 * Files hang off almost every entity in the app (matters, tasks, contacts,
 * review playbooks), and every endpoint that returns them picked its own
 * casing and its own field names: `FileName` here, `file_name` there,
 * `filename` in a third place; an uploader that is sometimes a string,
 * sometimes `{ first_name, last_name }`, sometimes absent. Each page did its
 * own reading, so the same file rendered with a different name, a different
 * date format, and a different uploader depending on where you opened it.
 *
 * This file is the one reading. A raw row goes in, a normalized `Attachment`
 * comes out, and the display layer only has to draw it. It is pure and
 * covered by tests because it is where a rename on the backend can silently
 * start showing every user "Untitled".
 */

/** One file, ready to draw. */
export interface Attachment {
  id: string;
  /** File name including extension, as uploaded. */
  name: string;
  /** Size in bytes. Undefined when the backend does not report one. */
  size?: number;
  /** MIME type or a bare file-type token ("pdf") — whatever the row carried. */
  mime?: string;
  /** Display name of whoever uploaded it. Empty when unattributed. */
  uploadedBy?: string;
  /** ISO-ish timestamp; rendered through `feedTime`. */
  uploadedAt?: string;
  /** Direct/href download target, when the backend hands one out. */
  url?: string;
  /** The row it came from — hosts need it to call their own endpoints. */
  raw?: any;
}

/** A raw attachment row, in every shape a backend endpoint returns one. */
export type RawAttachment = Record<string, any>;

/**
 * Field aliases, most-specific first. Order matters: a row that carries both
 * `filename` and `name` means the first as the file and the second as a
 * label, so the file-ish key has to win.
 */
const ID_KEYS = [
  'AttachmentID',
  'attachment_id',
  'attachmentId',
  'document_uuid',
  'documentUuid',
  'file_id',
  'FileID',
  'uuid',
  'id',
  'ID',
];

const NAME_KEYS = [
  'FileName',
  'file_name',
  'fileName',
  'filename',
  'document_name',
  'source_document_name',
  'original_name',
  'originalName',
  'title',
  'Name',
  'name',
];

const SIZE_KEYS = [
  'FileSize',
  'file_size',
  'fileSize',
  'size_bytes',
  'sizeBytes',
  'bytes',
  'size',
];

const MIME_KEYS = [
  'mime_type',
  'mimeType',
  'content_type',
  'contentType',
  'ContentType',
  'FileType',
  'file_type',
  'fileType',
  'mime',
  'type',
];

const DATE_KEYS = [
  'uploaded_at',
  'uploadedAt',
  'UploadedAt',
  'upload_date',
  'date_uploaded',
  'created_at',
  'createdAt',
  'CreatedAt',
  'timestamp',
];

const URL_KEYS = [
  'FileURL',
  'file_url',
  'fileUrl',
  'download_url',
  'downloadUrl',
  'DownloadURL',
  'signed_url',
  'signedUrl',
  'url',
  'URL',
];

const UPLOADER_KEYS = [
  'uploaded_by_user_name',
  'uploadedByUserName',
  'uploaded_by_name',
  'uploader_name',
  'uploadedBy',
  'uploaded_by',
  'UploadedBy',
  'created_by_name',
  'created_by',
  'user_name',
  'user',
];

/** The array of rows, whichever envelope the endpoint wrapped it in. */
const LIST_KEYS = ['attachments', 'documents', 'files', 'results', 'items', 'data'];

const firstValue = (row: RawAttachment, keys: string[]): any => {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
};

/** A person is a string on some endpoints and an object on others. */
const readPerson = (value: any): string => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value !== 'object') return '';

  const full =
    value.full_name ||
    value.fullName ||
    value.display_name ||
    value.displayName ||
    value.name ||
    value.user_name;
  if (typeof full === 'string' && full.trim()) return full.trim();

  const first = value.first_name || value.firstName || value.FirstName || '';
  const last = value.last_name || value.lastName || value.LastName || '';
  const joined = `${first} ${last}`.trim();
  if (joined) return joined;

  const email = value.email || value.Email;
  return typeof email === 'string' ? email.trim() : '';
};

/** Sizes arrive as numbers, as numeric strings, and occasionally as "0". */
const readSize = (value: any): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? value : undefined;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  }
  return undefined;
};

const readTimestamp = (value: any): string | undefined => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  // Epoch seconds/milliseconds show up on a couple of the older endpoints.
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value < 1e12 ? value * 1000 : value;
    const date = new Date(ms);
    return isNaN(date.getTime()) ? undefined : date.toISOString();
  }
  return undefined;
};

/** Lowercase extension without the dot; empty when the name carries none. */
export const attachmentExtension = (attachment: Pick<Attachment, 'name' | 'mime'>): string => {
  const name = attachment.name || '';
  const dot = name.lastIndexOf('.');
  if (dot > 0 && dot < name.length - 1) {
    return name.slice(dot + 1).toLowerCase();
  }
  // `FileType` is often a bare token ("pdf", "docx") rather than a MIME type.
  const mime = (attachment.mime || '').toLowerCase();
  if (mime && !mime.includes('/')) return mime;
  return '';
};

/** "2.4 MB". Returns an empty string when the size is unknown. */
export const formatFileSize = (bytes?: number): string => {
  if (bytes === undefined || bytes === null || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 || Number.isInteger(value) ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
};

/**
 * Normalize one raw row.
 *
 * `index` only feeds the fallback id: a row with no identifier at all still
 * has to get a stable React key, and "attachment-3" beats a crash.
 */
export const toAttachment = (row: RawAttachment | null | undefined, index = 0): Attachment => {
  if (!row || typeof row !== 'object') {
    return { id: `attachment-${index}`, name: '' };
  }

  const rawId = firstValue(row, ID_KEYS);
  const name = firstValue(row, NAME_KEYS);
  const uploader = firstValue(row, UPLOADER_KEYS);

  return {
    id: rawId !== undefined ? String(rawId) : `attachment-${index}`,
    name: typeof name === 'string' ? name : name !== undefined ? String(name) : '',
    size: readSize(firstValue(row, SIZE_KEYS)),
    mime: (() => {
      const mime = firstValue(row, MIME_KEYS);
      return typeof mime === 'string' && mime.trim() ? mime.trim() : undefined;
    })(),
    uploadedBy: readPerson(uploader) || undefined,
    uploadedAt: readTimestamp(firstValue(row, DATE_KEYS)),
    url: (() => {
      const url = firstValue(row, URL_KEYS);
      return typeof url === 'string' && url.trim() ? url.trim() : undefined;
    })(),
    raw: row,
  };
};

/**
 * Pull the rows out of whatever envelope the endpoint used, then normalize.
 *
 * Accepts a bare array, `{ data: [...] }`, `{ attachments: [...] }`,
 * `{ data: { documents: [...] } }` — every wrapper currently in use — and an
 * error/empty response, which yields `[]` rather than throwing into a render.
 */
export const toAttachments = (payload: any): Attachment[] => {
  const rows = extractAttachmentRows(payload);
  return rows.map((row, index) => toAttachment(row, index));
};

/** The un-normalized rows, for callers that still need the raw shape. */
export const extractAttachmentRows = (payload: any, depth = 0): RawAttachment[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload !== 'object') return [];
  // One level of `{ data: { attachments: [...] } }` nesting, not an open walk.
  if (depth > 2) return [];

  for (const key of LIST_KEYS) {
    const value = payload[key];
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') {
      const nested = extractAttachmentRows(value, depth + 1);
      if (nested.length) return nested;
    }
  }
  return [];
};

/** Newest first — the order every attachment surface shows files in. */
export const sortAttachments = (attachments: Attachment[]): Attachment[] =>
  [...attachments].sort((a, b) => {
    const at = a.uploadedAt ? new Date(a.uploadedAt).getTime() : NaN;
    const bt = b.uploadedAt ? new Date(b.uploadedAt).getTime() : NaN;
    const aValid = !isNaN(at);
    const bValid = !isNaN(bt);
    // Undated rows sink rather than scrambling the dated ones around them.
    if (!aValid && !bValid) return (a.name || '').localeCompare(b.name || '');
    if (!aValid) return 1;
    if (!bValid) return -1;
    return bt - at;
  });

/** Case-insensitive match on file name and uploader — the list's search box. */
export const filterAttachments = (attachments: Attachment[], query: string): Attachment[] => {
  const needle = (query || '').trim().toLowerCase();
  if (!needle) return attachments;
  return attachments.filter(
    (a) =>
      (a.name || '').toLowerCase().includes(needle) ||
      (a.uploadedBy || '').toLowerCase().includes(needle)
  );
};
